<template>
  <v-dialog v-model="visible" persistent max-width="480">
    <v-card class="revolut-pay-checkout">
      <v-card-title class="headline">Pay with Revolut</v-card-title>
      <v-card-text>
        <p v-if="paymentDeadline && !verifying">Complete payment by {{ paymentDeadline }}.</p>
        <p v-if="message" class="revolut-pay-checkout__message">{{ message }}</p>
        <div
          v-show="(!status || status === 'PENDING') && !verifying"
          ref="button"
          class="revolut-pay-checkout__button"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          v-if="verifying && !statusPollDeadline"
          flat
          :disabled="loading"
          @click="beginStatusVerification"
        >
          Check status
        </v-btn>
        <v-btn flat color="primary" :disabled="loading || awaitingFinalization" @click="close">
          {{ closeLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { EventBus } from '@/plugins/event-bus'
import { getPaymentAttemptStatus, getRevolutReturnStatus, cancelPaymentAttempt } from '@/http'
import { setRevolutCheckoutActive } from '@/revolut-checkout-state'

const isRevolutCheckout = checkout =>
  checkout &&
  checkout.provider === 'REVOLUT' &&
  checkout.checkoutType === 'REVOLUT_PAY' &&
  Number.isSafeInteger(checkout.attemptId) &&
  checkout.statusToken &&
  checkout.returnToken &&
  checkout.orderToken &&
  checkout.publicKey &&
  Number.isSafeInteger(checkout.amountMinor) &&
  checkout.amountMinor > 0 &&
  /^[A-Z]{3}$/.test(String(checkout.currency || '')) &&
  ['sandbox', 'prod'].includes(checkout.environment) &&
  ['en', 'hr'].includes(checkout.locale)

export default {
  name: 'revolut-pay-checkout',
  data() {
    return {
      visible: false,
      loading: false,
      message: '',
      status: null,
      checkout: null,
      completions: [],
      payments: null,
      revolutPay: null,
      finalized: false,
      verifying: false,
      statusPollTimer: null,
      statusPollDeadline: null,
    }
  },
  computed: {
    paymentDeadline() {
      const deadline = this.checkout && new Date(this.checkout.expiresAt)

      return deadline && Number.isFinite(deadline.getTime())
        ? deadline.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null
    },
    closeLabel() {
      if (this.finalized) return 'Continue'
      if (['FAILED', 'CANCELED', 'COMPLETED', 'REFUNDED'].includes(this.status)) return 'Close'

      return 'Cancel payment'
    },
    awaitingFinalization() {
      return this.status === 'COMPLETED' && !this.finalized && !!this.statusPollDeadline
    },
  },
  mounted() {
    EventBus.$on('revolut-pay:open', this.open)
    this.resumeMobileRedirect()
  },
  beforeDestroy() {
    EventBus.$off('revolut-pay:open', this.open)
    this.clearStatusPolling()
    this.destroySdk()
    this.checkout = null
    setRevolutCheckoutActive(false)
  },
  methods: {
    async open({ checkout, resolve, reject }) {
      if (!isRevolutCheckout(checkout)) {
        reject(new Error('Revolut Pay checkout response is invalid'))

        return
      }
      if (this.checkout) {
        if (this.checkout.attemptId === checkout.attemptId && this.checkout.orderToken === checkout.orderToken) {
          this.completions.push({ resolve, reject })
        } else {
          reject(new Error('Another payment checkout is already open'))
        }

        return
      }
      this.destroySdk()
      setRevolutCheckoutActive(true)
      this.checkout = checkout
      this.completions = [{ resolve, reject }]
      window.sessionStorage.setItem('revolut-pay-checkout', JSON.stringify(checkout))
      this.visible = true
      this.loading = true
      this.message = ''
      this.status = null
      this.finalized = false
      this.verifying = false
      this.clearStatusPolling()

      try {
        await this.$nextTick()
        const module = await import('@revolut/checkout')
        const RevolutCheckout = module.default || module
        this.payments = await RevolutCheckout.payments({
          publicToken: checkout.publicKey,
          mode: checkout.environment,
          locale: checkout.locale,
        })
        this.revolutPay = this.payments.revolutPay
        this.revolutPay.mount(this.$refs.button, {
          currency: checkout.currency,
          totalAmount: checkout.amountMinor,
          // The order was created and persisted by the backend before this UI opens.
          createOrder: async () => ({ publicId: checkout.orderToken }),
          buttonStyle: { size: 'large', radius: 'small', action: 'pay' },
          mobileRedirectUrls: this.mobileRedirectUrls(),
        })
        this.revolutPay.on('payment', this.handlePaymentEvent)
      } catch (error) {
        console.error('Unable to initialise Revolut Pay:', error)
        this.message = 'Revolut Pay could not be opened. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async handlePaymentEvent(event) {
      if (!event || event.type === 'cancel') {
        this.message = 'Revolut payment window was closed. Checking payment status…'
        this.beginStatusVerification()

        return
      }
      if (event.type === 'error') {
        this.message = 'Revolut reported a payment error. Checking payment status…'
        this.beginStatusVerification()

        return
      }
      if (event.type !== 'success') return

      this.beginStatusVerification()
    },
    async close() {
      if (this.loading || this.awaitingFinalization) return
      if (this.checkout && !['FAILED', 'CANCELED', 'COMPLETED', 'REFUNDED'].includes(this.status)) {
        this.clearStatusPolling()
        this.loading = true
        try {
          const observed = await cancelPaymentAttempt(
            this.checkout.attemptId,
            this.checkout.statusToken,
          )
          this.status = observed && observed.status
          if (observed && observed.finalized && observed.successUrl) {
            this.completeCheckout(observed.successUrl)

            return
          }
          if (!['FAILED', 'CANCELED'].includes(this.status)) {
            this.beginStatusVerification()

            return
          }
        } catch (error) {
          this.message =
            'Cancellation could not be confirmed. Checking payment status; please do not pay again.'
          this.beginStatusVerification()

          return
        } finally {
          if (!this.statusPollDeadline) this.loading = false
        }
      }
      const completions = this.completions
      const result = { status: this.status || 'PENDING' }
      this.destroySdk()
      this.clearStatusPolling()
      this.visible = false
      this.checkout = null
      this.completions = []
      this.message = ''
      this.status = null
      this.finalized = false
      this.verifying = false
      if (['FAILED', 'CANCELED', 'REFUNDED'].includes(result.status)) {
        setRevolutCheckoutActive(false)
        window.sessionStorage.removeItem('revolut-pay-checkout')
      }
      completions.forEach(completion => completion.resolve(result))
    },
    mobileRedirectUrls() {
      const url = new URL(window.location.href)
      url.searchParams.set('revolutPaymentReturn', '1')
      url.searchParams.set('revolutAttempt', String(this.checkout.attemptId))
      url.searchParams.set('revolutReturnToken', this.checkout.returnToken)
      const redirect = url.toString()

      return { success: redirect, failure: redirect, cancel: redirect }
    },
    async resumeMobileRedirect() {
      const url = new URL(window.location.href)
      if (url.searchParams.get('revolutPaymentReturn') !== '1') return
      const returnAttemptId = Number(url.searchParams.get('revolutAttempt'))
      const returnToken = url.searchParams.get('revolutReturnToken') || ''
      const hasReturnCredentials = Number.isSafeInteger(returnAttemptId) && returnAttemptId > 0 && returnToken.length >= 32
      url.searchParams.delete('revolutPaymentReturn')
      url.searchParams.delete('revolutAttempt')
      url.searchParams.delete('revolutReturnToken')
      url.searchParams.delete('_rp_oid')
      window.history.replaceState({}, document.title, url.toString())
      try {
        const checkout = JSON.parse(window.sessionStorage.getItem('revolut-pay-checkout') || 'null')
        const storedCheckoutIsValid = isRevolutCheckout(checkout)
        if (!storedCheckoutIsValid && !hasReturnCredentials) {
          this.visible = true
          this.message = 'This payment return cannot be verified. Please contact support before trying to pay again.'

          return
        }
        this.checkout = storedCheckoutIsValid ? checkout : { attemptId: returnAttemptId, returnToken }
        // The SDK may return in a new tab. Reconcile the attempt before showing
        // a success page, using only the read-only return token when needed.
        this.visible = false
        this.loading = true
        this.message = 'Payment returned from Revolut. Verifying its status…'
        this.beginStatusVerification()
      } catch (error) {
        console.error('Unable to resume Revolut Pay status check:', error)
      }
    },
    beginStatusVerification() {
      setRevolutCheckoutActive(true)
      this.clearStatusPolling()
      this.verifying = true
      // A provider success is not sufficient to issue tickets. Give the signed
      // webhook/finalisation worker time to finish, then keep the order pending
      // instead of falsely showing a successful purchase.
      this.statusPollDeadline = Date.now() + 90000
      this.refreshPaymentStatus()
    },
    clearStatusPolling() {
      if (this.statusPollTimer) window.clearTimeout(this.statusPollTimer)
      this.statusPollTimer = null
      this.statusPollDeadline = null
    },
    scheduleStatusRefresh() {
      if (!this.statusPollDeadline) return
      if (Date.now() >= this.statusPollDeadline) {
        this.statusPollDeadline = null
        this.message =
          this.status === 'COMPLETED'
            ? 'Payment is confirmed, but your order is still being completed. Please do not pay again. Check status shortly.'
            : 'Payment status has not been confirmed yet. Please do not pay again. Check status shortly.'

        return
      }
      this.statusPollTimer = window.setTimeout(() => this.refreshPaymentStatus(), 1500)
    },
    completeCheckout(successUrl) {
      // Keep the countdown suppressed until navigation actually replaces the page.
      setRevolutCheckoutActive(true)
      this.clearStatusPolling()
      this.finalized = true
      window.sessionStorage.removeItem('revolut-pay-checkout')
      this.message = 'Payment confirmed. Opening your order…'
      this.destroySdk()
      window.location.assign(successUrl)
    },
    async refreshPaymentStatus() {
      const checkout = this.checkout
      if (!checkout) return
      this.loading = true
      try {
        const observed = checkout.statusToken
          ? await getPaymentAttemptStatus(checkout.attemptId, checkout.statusToken)
          : await getRevolutReturnStatus(checkout.attemptId, checkout.returnToken)
        if (this.checkout !== checkout) return
        this.status = observed && observed.status
        if (observed && observed.finalized && observed.successUrl) {
          this.completeCheckout(observed.successUrl)

          return
        }
        if (['FAILED', 'CANCELED', 'REFUNDED'].includes(this.status)) {
          this.clearStatusPolling()
          this.destroySdk()
          setRevolutCheckoutActive(false)
          window.sessionStorage.removeItem('revolut-pay-checkout')
          this.message =
            this.status === 'FAILED'
              ? 'Payment failed or expired. You can close this window and start a new checkout.'
              : this.status === 'CANCELED'
                ? 'Payment was cancelled.'
                : 'Payment was refunded.'

          return
        }
        this.message =
          this.status === 'COMPLETED'
            ? 'Payment is confirmed. Completing your order…'
            : 'Payment is being verified. Please do not submit another payment.'
      } catch (error) {
        if (this.checkout !== checkout) return
        console.error('Unable to refresh Revolut payment status:', error)
        this.message = 'Payment is being verified. Please do not submit another payment.'
      } finally {
        if (this.checkout === checkout) {
          this.loading = false
          if (!this.finalized) this.scheduleStatusRefresh()
        }
      }
    },
    destroySdk() {
      if (this.revolutPay && this.revolutPay.destroy) this.revolutPay.destroy()
      if (this.payments && this.payments.destroy) this.payments.destroy()
      this.revolutPay = null
      this.payments = null
    },
  },
}
</script>

<style scoped>
.revolut-pay-checkout__message {
  margin: 0 0 16px;
}

.revolut-pay-checkout__button {
  min-height: 48px;
}
</style>
